import { ChangeDetectionStrategy, Component, computed, input, output, signal } from '@angular/core';
import { RouterModule } from '@angular/router';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { BoxShape, SHAPE_CHOICES } from '../../shared/shapes';
import {
  lucideChevronDown,
  lucideChevronRight,
  lucideChevronUp,
  lucideCircle,
  lucideCircleCheck,
  lucideCircleUser,
  lucideDiamond,
  lucideImage,
  lucideLock,
  lucideMinus,
  lucidePackage,
  lucidePanelTop,
  lucidePlus,
  lucideQrCode,
  lucideShapes,
  lucideSparkles,
  lucideSquare,
  lucideStar,
  lucideTriangle,
  lucideTriangleAlert,
  lucideType,
  lucideWandSparkles,
} from '@ng-icons/lucide';
import { HlmBadgeImports } from '@spartan-ng/helm/badge';
import { HlmTooltipImports } from '@spartan-ng/helm/tooltip';
import { HlmButtonImports } from '@spartan-ng/helm/button';
import { HlmCardImports } from '@spartan-ng/helm/card';
import { HlmTypographyImports } from '@spartan-ng/helm/typography';
import {
  CatalogEntry,
  DesignCatalog,
  DesignElement,
  Placeholder,
  PlaceholderSource,
  VoucherType,
} from '../../../../services/voucher-design.service';
import { BrandingFiles, allowedOn, placeholderMap, requiredFor } from '../../editor-geometry';
import {
  ADDRESS_LINE,
  placeholderKeys,
  renderOf,
  titleOf,
  wouldPrint,
} from '../../shared/placeholders';
import { BRAND_KIND, ChecklistEntry, TextPreset } from '../../voucher-design-editor.store';

/** One tile of "Hinzufügen". */
interface Tile {
  key: string;
  label: string;
  icon: string;
  /** Already on the page (a singleton): the tile finds it instead of adding another. */
  placedId: string | null;
  /** Why it would print nothing right now (no logo uploaded). */
  hint: string;
  action: 'text' | 'picture' | 'kind' | 'shapes';
}

/** One value in the "Werte" list. */
interface ValueRow {
  placeholder: Placeholder;
  preset: TextPreset;
  placedId: string | null;
  required: boolean;
}

/** A ready-made text of several values, listed with them: the address as one line. */
interface TextRow {
  label: string;
  preset: TextPreset;
  /** The values it is made of; placed when some text carries them all. */
  keys: string[];
  placedId: string | null;
}

/** One element in the "Ebenen" list, top of the stack first. */
interface Layer {
  id: string;
  title: string;
  icon: string;
  selected: boolean;
  /** Prints nothing for this company. */
  muted: boolean;
  /** The Schenkwerk logo: always on top, not reordered. */
  pinned: boolean;
  canFront: boolean;
  canBack: boolean;
}

const SOURCE_LABELS: Record<PlaceholderSource, string> = {
  voucher: 'Vom Gutschein',
  company: 'Aus deinen Einstellungen',
  product: 'Vom Produkt',
};

const TEXT_PRESET: TextPreset = { content: 'Text', wrap: false, edit: true };

/**
 * The toolbox: what is still required, what can be added (tiles), the values a
 * text can carry (a list), and the layers of the page in stacking order.
 */
@Component({
  selector: 'app-element-palette',
  standalone: true,
  imports: [
    RouterModule,
    NgIcon,
    HlmBadgeImports,
    HlmButtonImports,
    HlmCardImports,
    HlmTooltipImports,
    HlmTypographyImports,
  ],
  providers: [
    provideIcons({
      lucideChevronDown,
      lucideChevronRight,
      lucideChevronUp,
      lucideCircle,
      lucideCircleCheck,
      lucideCircleUser,
      lucideDiamond,
      lucideImage,
      lucideLock,
      lucideMinus,
      lucidePackage,
      lucidePanelTop,
      lucidePlus,
      lucideQrCode,
      lucideShapes,
      lucideSparkles,
      lucideSquare,
      lucideStar,
      lucideTriangle,
      lucideTriangleAlert,
      lucideType,
      lucideWandSparkles,
    }),
  ],
  templateUrl: './element-palette.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ElementPaletteComponent {
  readonly catalog = input<DesignCatalog | null>(null);
  readonly checklist = input<ChecklistEntry[]>([]);
  readonly elements = input<DesignElement[]>([]);
  readonly selectedIds = input<ReadonlySet<string>>(new Set());
  readonly voucherType = input<VoucherType>('money');
  readonly branding = input<BrandingFiles>({ logo: true, banner: true });

  /** A non-text kind by its name (QR code, logo, banner, product image). */
  readonly add = output<string>();
  /** A text with ready content: own words or a value. */
  readonly addText = output<TextPreset>();
  /** A shape from the shape row: a box element with that shape. */
  readonly addShape = output<BoxShape>();
  readonly select = output<string>();
  /** A library picture: the editor opens the library, then places the pick. */
  readonly pickImage = output<void>();
  /** Place everything required that is missing at once. */
  readonly addRequired = output<void>();
  /** Move an element in the stacking order: +1 towards the front, -1 towards the back. */
  readonly move = output<{ id: string; delta: number }>();

  readonly shapeChoices = SHAPE_CHOICES;
  readonly shapesOpen = signal(false);
  readonly textPreset = TEXT_PRESET;

  readonly placedRequired = computed(() => this.checklist().filter((i) => i.placedId).length);
  readonly missingRequired = computed(() => this.checklist().filter((i) => !i.placedId));
  readonly missingLabels = computed(() =>
    this.missingRequired()
      .map((i) => i.label)
      .join(', '),
  );
  private readonly known = computed(() => placeholderMap(this.catalog()));
  private readonly byKind = computed(
    () => new Map((this.catalog()?.elements ?? []).map((e) => [e.kind, e])),
  );
  private readonly placedByKind = computed(() => {
    const map = new Map<string, string>();
    for (const e of this.elements()) if (!map.has(e.kind)) map.set(e.kind, e.id);
    return map;
  });

  /** Text, the pictures a design of this type may show, the QR code, shapes. */
  readonly tiles = computed<Tile[]>(() => {
    const catalog = this.catalog();
    if (!catalog) return [];
    const icons: Record<string, string> = {
      library: 'lucideImage',
      logo: 'lucideCircleUser',
      banner: 'lucidePanelTop',
      product: 'lucidePackage',
    };
    const tiles: Tile[] = [
      { key: 'text', label: 'Text', icon: 'lucideType', placedId: null, hint: '', action: 'text' },
    ];
    for (const entry of catalog.elements) {
      if (!allowedOn(entry, this.voucherType())) continue;
      if (entry.render === 'image') {
        tiles.push({
          key: entry.kind,
          label: this.shortLabel(entry),
          icon: icons[entry.image_source ?? 'library'] ?? 'lucideImage',
          placedId: entry.singleton ? (this.placedByKind().get(entry.kind) ?? null) : null,
          hint: this.missingHint(entry),
          action: entry.image_source === 'library' ? 'picture' : 'kind',
        });
      } else if (entry.render === 'qr') {
        tiles.push({
          key: entry.kind,
          label: 'QR-Code',
          icon: 'lucideQrCode',
          placedId: this.placedByKind().get(entry.kind) ?? null,
          hint: '',
          action: 'kind',
        });
      }
    }
    tiles.push({
      key: 'shapes',
      label: 'Form',
      icon: 'lucideShapes',
      placedId: null,
      hint: '',
      action: 'shapes',
    });
    return tiles;
  });

  private shortLabel(entry: CatalogEntry): string {
    return { logo: 'Logo', banner: 'Banner', product: 'Produktbild', library: 'Bild' }[
      entry.image_source ?? 'library'
    ];
  }

  /** The address as one text of its parts, listed with the company's values. */
  readonly addressRow = computed<TextRow | null>(() => {
    const known = this.known();
    const keys = placeholderKeys(ADDRESS_LINE, known);
    if (keys.length < 4) return null;
    const placed = this.elements().find((e) => {
      if (e.kind !== 'text') return false;
      const used = placeholderKeys(e.content ?? '', known);
      return keys.every((key) => used.includes(key));
    });
    return {
      label: 'Adresse in einer Zeile',
      preset: { content: ADDRESS_LINE, wrap: false, size: { width: 520, height: 22 } },
      keys,
      placedId: placed?.id ?? null,
    };
  });

  /** The values a text can carry, grouped by where they come from. */
  readonly valueGroups = computed<{ label: string; rows: ValueRow[] }[]>(() => {
    const catalog = this.catalog();
    if (!catalog) return [];
    const type = this.voucherType();
    const required = requiredFor(catalog, type).placeholders;
    const known = this.known();
    const texts = this.elements().filter((e) => e.kind === 'text');
    const rows = catalog.placeholders
      .filter((p) => type === 'product' || !p.product_only)
      .map((p) => ({
        placeholder: p,
        preset: { content: p.preset, wrap: p.wrap, size: p.default_size },
        placedId:
          texts.find((e) => placeholderKeys(e.content ?? '', known).includes(p.key))?.id ?? null,
        required: required.includes(p.key),
      }));
    return (['voucher', 'company', 'product'] as PlaceholderSource[])
      .map((source) => ({
        label: SOURCE_LABELS[source],
        rows: rows.filter((row) => row.placeholder.source === source),
      }))
      .filter((group) => group.rows.length > 0);
  });

  /** The page's elements, the topmost first, as a design tool lists its layers. */
  readonly layers = computed<Layer[]>(() => {
    const elements = this.elements();
    const known = this.known();
    const selected = this.selectedIds();
    const movable = elements.filter((e) => e.kind !== BRAND_KIND).length;
    return elements
      .map((e, index): Layer => {
        const entry = this.byKind().get(e.kind);
        const render = renderOf(e, entry);
        const shape = SHAPE_CHOICES.find((s) => s.value === e.shape)?.icon;
        const icon =
          render === 'image'
            ? 'lucideImage'
            : render === 'qr'
              ? 'lucideQrCode'
              : render === 'box'
                ? (shape ?? 'lucideSquare')
                : render === 'brand'
                  ? 'lucideSparkles'
                  : 'lucideType';
        return {
          id: e.id,
          title: titleOf(e, entry, known),
          icon,
          selected: selected.has(e.id),
          muted: entry?.render === 'text' && !wouldPrint(e.content ?? '', known),
          pinned: e.kind === BRAND_KIND,
          canFront: index < movable - 1,
          canBack: index > 0,
        };
      })
      .reverse();
  });

  onTile(tile: Tile) {
    if (tile.placedId) {
      this.select.emit(tile.placedId);
      return;
    }
    switch (tile.action) {
      case 'text':
        this.addText.emit(TEXT_PRESET);
        break;
      case 'picture':
        this.pickImage.emit();
        break;
      case 'kind':
        this.add.emit(tile.key);
        break;
      case 'shapes':
        this.shapesOpen.update((open) => !open);
        break;
    }
  }

  onShape(shape: BoxShape) {
    this.shapesOpen.set(false);
    this.addShape.emit(shape);
  }

  /** Why a value would print nothing right now: the account has no such value. */
  valueHint(row: ValueRow): string {
    return row.placeholder.has_value === false ? 'Noch kein Wert in den Einstellungen' : '';
  }

  /** Why a logo or banner element would print nothing right now. */
  private missingHint(entry: CatalogEntry): string {
    if (entry.image_source === 'logo' && !this.branding().logo) return 'Noch kein Logo hochgeladen';
    if (entry.image_source === 'banner' && !this.branding().banner)
      return 'Noch kein Banner hochgeladen';
    return '';
  }
}
